import React, { FunctionComponent, useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { SurveyContext } from '@context/SurveyContext'
import { PredictionContext } from '@context/PredictionContext'
import { NavigationContext } from '@context/NavigationContext'
import { SurveyMode } from '@reducers/SurveyReducer'
import { AnswerObject, AnswerType } from '@models/Survey/Answer'
import { TabProps } from '@components/Tab/Tab'
import { TabbedPane } from '@components/Survey/TabbedPane/TabbedPane'
import QuestionsBrowser from '@components/Survey/QuestionsBrowser/QuestionsBrowser'
import { SandboxQuestionnairePicker } from '@components/Survey/Sandbox/SandboxQuestionnairePicker'
import './Sandbox.scss'
import { usePreventBackNavigation } from '@hooks/usePreventBackNavigation'
import { isLiteraryApp } from '@models/AppType'
import { GenderBookGoToWebsiteHeader } from '../GenderBookGoToWebsite/GenderBookGoToWebsiteHeader'
import ShareModal from '@hoc/WithMenu/ShareModal'
import { useEnsureSurveyCompleted } from '@hooks/useEnsureSurveyCompleted'

export function Sandbox() {
  useEnsureSurveyCompleted()
  usePreventBackNavigation()
  const { t, i18n } = useTranslation('Simulation')
  const {
    mode,
    answerObject,
    questionsList,
    setQuestion,
    currentQuestion,
    setAnswerObject,
    setAnswer,
    setMode,
    submitSandbox,
    resetSandbox,
    survey,
    resultsSnapshotsBySurveyKey = {},
  } = useContext(SurveyContext)
  const { results, sandboxedResults } = useContext(PredictionContext)
  const { setPrevHandler, setNextHandler, setResetHandler } = useContext(NavigationContext)
  const [originalAnswerObject, setOriginalAnswerObject] = useState<AnswerObject>(answerObject)
  const [currentTabs, setCurrentTabs] = useState<TabProps[]>([])
  const [canSubmit, setCanSubmit] = useState(false)

  const surveySnapshot = survey?.key ? resultsSnapshotsBySurveyKey[survey.key] : undefined
  const predictionForBars =
    sandboxedResults ??
    (surveySnapshot && typeof surveySnapshot === 'object' && surveySnapshot !== null && 'prediction' in surveySnapshot
      ? (surveySnapshot as typeof results)
      : undefined) ??
    results

  useEffect(() => {
    setMode(SurveyMode.SANDBOX_PORTRAIT)

    // We keep in state the user original answers and reset them once we get out of the simulation
    setOriginalAnswerObject(answerObject)

    if (!currentQuestion || currentQuestion.key.includes('tactical')) {
      setQuestion(questionsList[0]?.id)
    }

    if (!results && !sandboxedResults) {
      // we're submitting sandbox in case that we refresh and dont have any result yet
      submitSandbox()
    }

    return () => {
      setMode(SurveyMode.DEFAULT)
      setAnswerObject(originalAnswerObject)
      setPrevHandler(undefined)
      setNextHandler(undefined)
      setResetHandler(undefined)
    }
  }, [])

  useEffect(() => {
    setCurrentTabs([
      {
        text: t('navigation.simulator'),
        active: false,
      },
    ])
  }, [mode, t])

  useEffect(() => {
    if (canSubmit) {
      submitSandbox()
    }
  }, [canSubmit, answerObject])

  const handleSetQuestion = (questionId: AnswerType): void => {
    setQuestion(questionId as string)
  }

  const handleResetAnswers = () => {
    setCanSubmit(false)
    setAnswerObject(originalAnswerObject)
    resetSandbox(originalAnswerObject)
    setResetHandler(undefined)
  }

  const handleSetAnswer = (value: AnswerType): void => {
    setAnswer(value)
    setResetHandler(handleResetAnswers)
    setCanSubmit(true)
  }

  const firstPrediction = isLiteraryApp
    ? predictionForBars?.prediction &&
      'data' in predictionForBars.prediction &&
      predictionForBars.prediction.data.reduce(function (acc: { value: number }, item: { value: number }) {
        return acc && acc.value > item.value ? acc : item
      })
    : undefined
  return (
    <>
      <TabbedPane tabs={currentTabs} predictionResults={predictionForBars} propClassName="Simulation">
        {firstPrediction && (
          <div className="Sandbox-TopSection">
            <GenderBookGoToWebsiteHeader
              results={predictionForBars}
              gender={firstPrediction?.names?.full[i18n.language]}
            />
          </div>
        )}
        <SandboxQuestionnairePicker />
        <QuestionsBrowser
          key={survey?.key ? `qb-${survey.key}` : 'qb'}
          onSelection={handleSetQuestion}
          onAnswer={handleSetAnswer}
        />
        <ShareModal />
      </TabbedPane>
    </>
  )
}

export default Sandbox
